import React from "react";
import {
    Avatar, Box, Button, Chip, CircularProgress, Divider, Grid, Rating, Stack, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EmailIcon from "@mui/icons-material/Email";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HomeIcon from "@mui/icons-material/Home";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SecurityIcon from "@mui/icons-material/Security";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import SmsIcon from "@mui/icons-material/Sms";
import StarRateIcon from "@mui/icons-material/StarRate";
import HistoryIcon from "@mui/icons-material/History";
import StoreIcon from "@mui/icons-material/Store";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SegmentIcon from "@mui/icons-material/Segment";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { alpha, useTheme } from "@mui/material/styles";
import type { Transaction } from "../../../../hooks/Clients/dashboard";
import useGetPersonHistory from "../../../../hooks/Clients/dashboard/useGetPersonHistory";
import PersonHistoryDialog from "./PersonHistoryDialog";
import { CUSTOMER_ACTIONS, useRights } from "../../../../rights";
import { Customer } from "../../../../types/Customer";
import type { Store } from "../../../../types/Store";
import { StoreDetailDialog } from "./CustomerStoreCard";
import { useStoreData } from "../../../../context/StoreDataContext";
import {
    AccountRaw, BadgeRaw, CanalChip, EventRaw, GroupMemberRaw, IdentifierRaw, OverflowTooltip,
    InfoCard, InfoRow, Offer, RealtimeOffer, SubscriptionRaw, TabPanel, TooltipCell,
    dataCellSx, formatAmount, formatDate, formatDateWithHours, headerCellSx, parseDMY, toArray,
} from "./ui";
import { useBrandConfig } from "../../../../context/BrandConfigContext";

interface SynthesisTabProps {
    activeTab: number;
    customer: Customer;
    fullAddress: string;
    postalStatusId: string | null;
    emailStatusId: string | null;
    emailStatusD: string | null;
    cellStatusId: string | null;
    cellStatusD: string | null;
    phoneStatusId: string | null;
    phoneStatusD: string | null;
    cardNumber: string | null;
    allIdentifiers: IdentifierRaw[];
    accounts: AccountRaw[];
    mainAccount: AccountRaw | null;
    npaiStatusDate: string | null;
    cnilCompanyMail: boolean;
    cnilCompanyEmail: boolean;
    cnilCompanySms: boolean;
    cnilTrustedMail: boolean;
    cnilTrustedEmail: boolean;
    cnilTrustedSms: boolean;
    subscriptions: SubscriptionRaw[];
    events: EventRaw[];
    groupMembers: GroupMemberRaw[];
    score: number | string | null;
    store: Store | null;
    loadingStore: boolean;
    realtimeTransactions: Transaction[];
    loadingRealtimeTx: boolean;
    realtimeOffers: RealtimeOffer[];
    loadingRealtimeOffers: boolean;
    offers: Offer[];
    loadingOffers: boolean;
    transactions: Transaction[];
    loadingTransac: boolean;
    setSelectedTx: (tx: Transaction | null) => void;
    setActiveTab: (tab: number) => void;
    // Absent si l'utilisateur n'a pas le droit : le bouton est masqué
    handleOpenMvt?: () => void;
    // Absent si l'utilisateur n'a pas le droit : le bouton est masqué
    handleOpenManualTransac?: () => void;
    handleToggleSubscription: (subscriptionId: string, currentValue: string, label: string) => void;
    loadingSubscriptionId?: string | null;
    t: (key: string) => string;
    personId?: string | number;
}

const SynthesisTab = ({
    activeTab, customer, fullAddress,
    postalStatusId, emailStatusId, emailStatusD, cellStatusId, cellStatusD, phoneStatusId, phoneStatusD,
    cardNumber,
    allIdentifiers, accounts, mainAccount,
    npaiStatusDate,
    cnilCompanyMail, cnilCompanyEmail, cnilCompanySms,
    cnilTrustedMail, cnilTrustedEmail, cnilTrustedSms,
    subscriptions, events, groupMembers, score,
    store,
    realtimeTransactions, loadingRealtimeTx,
    realtimeOffers, loadingRealtimeOffers,
    offers, loadingOffers,
    transactions, loadingTransac,
    setSelectedTx, setActiveTab,
    handleOpenMvt, handleOpenManualTransac, handleToggleSubscription, loadingSubscriptionId, t, personId,
}: SynthesisTabProps) => {
    const { wayTypes } = useStoreData();

    const getWayTypeName = (wayTypeId: string | null | undefined) => {
        if (!wayTypeId) return '';
        const wayType = wayTypes?.find(wt => wt.id.toString() === wayTypeId);
        return wayType ? wayType.name : '';
    };

    // Cartes de fidélité : types déclarés par l'enseigne (identifiers.retail.list), à la place d'un filtre sur le nom
    const { ready: brandReady, setting } = useBrandConfig();
    const retailTypes = setting('identifiers.retail.list');
    const visibleIdentifiers = brandReady
        ? allIdentifiers.filter(id => retailTypes.includes(String(id.PERSON_IDENTIFIER_TYPE_ID)))
        : allIdentifiers;

    const addrStreet3Line = [customer.ADDRESS3_NUM, getWayTypeName(customer.ADDRESS3_TYPE), customer.ADDRESS3_NAME].filter(Boolean).join(" ") || customer.ADDRESS3 || null;
    const addrOtherLine = [customer.ADDRESS1, customer.ADDRESS2, customer.ADDRESS4].filter(Boolean).join(" ") || null;
    const addrCityLine = [customer.ZIP_CODE, customer.LOCAL_CITY_NAME || customer.CITY].filter(Boolean).join(" ") || null;
    const addrCountryLine = customer.LOCAL_COUNTRY_NAME || customer.COUNTRY || null;

    // ── Badges API ────────────────────────────────────────────────────────────
    const imagesBase = process.env.REACT_APP_IMAGES_BASE_URL || "/images";
    const [historyOpen, setHistoryOpen] = React.useState(false);
    // Historique : da.customer.histo ou mode service client (BeOne : _personDescription.php:321)
    const { can } = useRights();
    const canViewHistory = can(CUSTOMER_ACTIONS.history);
    const theme = useTheme();
    const [storeDialogOpen, setStoreDialogOpen] = React.useState(false);
    const [storeDialogLoading, setStoreDialogLoading] = React.useState(false);
    const { history, isLoading: loadingHistory, error: historyError, getPersonHistory } = useGetPersonHistory();

    // Quand les données magasin arrivent, stopper le skeleton de la dialog
    React.useEffect(() => {
        if (store && storeDialogLoading) setStoreDialogLoading(false);
    }, [store, storeDialogLoading]);

    const handleOpenStoreDialog = React.useCallback(() => {
        if (!store) setStoreDialogLoading(true);
        setStoreDialogOpen(true);
    }, [store]);

    const handleOpenHistory = () => {
        if (personId) getPersonHistory(personId);
        setHistoryOpen(true);
    };


    // Palette catégorielle du thème (une teinte par badge)
    const badgeColors = theme.palette.chart;
    const badgeColor = (name?: string) => badgeColors[(name?.charCodeAt(0) ?? 0) % badgeColors.length];

    const BadgeImg = ({ name, isSegment }: { img?: string; name?: string; isSegment?: boolean }) => {
        return (
            <Avatar sx={{ width: 48, height: 48, bgcolor: badgeColor(name), display: "flex", alignItems: "center", justifyContent: "center" }}>
                {isSegment
                    ? <SegmentIcon sx={{ fontSize: 22, color: "common.white" }} />
                    : <WorkspacePremiumIcon sx={{ fontSize: 22, color: "common.white" }} />}
            </Avatar>
        );
    };

    // Badges seulement si l'enseigne a le module (module.badges.active)
    const apiBadges = brandReady && setting('module.badges.active')
        ? toArray<BadgeRaw>(customer.BADGES).filter(b => b.CUSTOMER_VISIBLE === "1")
        : [];

    const segmentBadges = apiBadges.filter(b => !b.IMG);

    // Grouper par CATEGORY_NAME
    const badgesByCategory = apiBadges.reduce<Record<string, BadgeRaw[]>>((acc, badge) => {
        const cat = badge.CATEGORY_NAME || "Autres";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(badge);
        return acc;
    }, {});
    const categoryEntries = Object.entries(badgesByCategory);
    const hasPersonalInfo = Boolean(
        customer.BIRTH_DATE
        || customer.STORE_NAME
        || customer.WEB_IDENTIFIER
        || customer.FORM_NUMBER
        || (score != null && !isNaN(Number(score)) && Number(score) > 0)
        || customer.EMAIL
        || customer.CELLPHONE
        || customer.PHONE
        || fullAddress
    );

    return (
    <>
    <TabPanel value={activeTab} index={0}>

        <Grid container spacing={2} alignItems="flex-start">

            <Grid size={{ xs: 12, md: 4 }}>
                <Stack spacing={2}>
                    {hasPersonalInfo && (
                        <Grid size={{ xs: 12 }}>
                            <InfoCard title={t("pages.customers.dashboard.synthesis.personalInfo")} icon={<PersonIcon />}>
                                {customer.STORE_NAME && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}><StoreIcon fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">{t("pages.customers.dashboard.synthesis.store")}</Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                                                onClick={handleOpenStoreDialog}
                                            >
                                                {String(customer.STORE_NAME)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label={t("pages.customers.dashboard.synthesis.birthDate")} value={(customer.BIRTH_DATE as string) || "—"} />
                                {customer.WEB_IDENTIFIER && (
                                    <InfoRow icon={<EmailIcon fontSize="small" />} label={t("pages.customers.dashboard.synthesis.webIdentifier")} value={customer.WEB_IDENTIFIER as string} />
                                )}
                                {customer.FORM_NUMBER && (
                                    <InfoRow icon={<ReceiptLongIcon fontSize="small" />} label="N° Formulaire" value={`${customer.FORM_NUMBER}${customer.FORM_LOT_NUMBER ? ` / ${customer.FORM_LOT_NUMBER}` : ""}`} />
                                )}
                                {score != null && !isNaN(Number(score)) && Number(score) > 0 && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}><StarRateIcon fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">Score</Typography>
                                            <Rating value={Number(score)} readOnly size="small" max={5} />
                                        </Box>
                                    </Box>
                                )}
                                {(customer.EMAIL || customer.CELLPHONE || customer.PHONE || fullAddress) && <Divider sx={{ my: 1.5 }} />}
                                {customer.EMAIL && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}><EmailIcon fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                                <Typography variant="body2" fontWeight={500}>{customer.EMAIL as string}</Typography>
                                                {["6","4","10","11","12"].includes(emailStatusId ?? "") && (
                                                    <Tooltip title={emailStatusD ? `Depuis le ${emailStatusD}` : ""} arrow>
                                                    <Chip label="Invalide" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                                                    </Tooltip>
                                                )}
                                                {emailStatusId === "-1" && (
                                                    <Tooltip title={emailStatusD ? `Depuis le ${emailStatusD}` : ""} arrow>
                                                        <Chip label="Email gelé" size="small" color="info" sx={{ height: 18, fontSize: 10, cursor: "default" }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                {customer.CELLPHONE && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}><SmartphoneIcon fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">Téléphone mobile</Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                                <Typography variant="body2" fontWeight={500}>{customer.CELLPHONE as string}</Typography>
                                                {cellStatusId === "4" && (
                                                    <Tooltip title={cellStatusD ? `Depuis le ${cellStatusD}` : ""} arrow>
                                                        <Chip label="Invalide" size="small" color="error" sx={{ height: 18, fontSize: 10, cursor: "default" }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                {customer.PHONE && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}><PhoneIcon fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">Téléphone fixe</Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                                <Typography variant="body2" fontWeight={500}>{customer.PHONE as string}</Typography>
                                                {phoneStatusId === "4" && (
                                                    <Tooltip title={phoneStatusD ? `Depuis le ${phoneStatusD}` : ""} arrow>
                                                        <Chip label="Invalide" size="small" color="error" sx={{ height: 18, fontSize: 10, cursor: "default" }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                {fullAddress && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}><HomeIcon fontSize="small" /></Box>
                                        <Box>
                                            {addrStreet3Line && <Typography variant="body2" display="block">{addrStreet3Line}</Typography>}
                                            {addrOtherLine && <Typography variant="body2" display="block">{addrOtherLine}</Typography>}
                                            {addrCityLine && <Typography variant="body2" display="block">{addrCityLine}</Typography>}
                                            {addrCountryLine && <Typography variant="body2" display="block">{addrCountryLine}</Typography>}
                                        </Box>
                                    </Box>
                                )}
                                {postalStatusId === "5" && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "error.main", mt: 0.2, flexShrink: 0 }}><WarningAmberIcon fontSize="small" /></Box>
                                        <Box>
                                            <Tooltip title={npaiStatusDate ? `Depuis le ${npaiStatusDate}` : ""} arrow>
                                                <Chip label="PND" size="small" color="error" sx={{ cursor: "default" }} />
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                )}
                                {postalStatusId === "6" && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                                        <Box sx={{ color: "error.main", mt: 0.2, flexShrink: 0 }}><WarningAmberIcon fontSize="small" /></Box>
                                        <Box>
                                            <Tooltip title={npaiStatusDate ? `Depuis le ${npaiStatusDate}` : ""} arrow>
                                                <Chip label="Adresse invalide" size="small" color="error" sx={{ cursor: "default" }} />
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                )}
                                {(customer.UPDATE_DATE || personId) && (
                                    <Box sx={{ mt: 1.5 }}>
                                        {customer.UPDATE_DATE && (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {t("pages.customers.dashboard.synthesis.lastContactModification")} : {customer.UPDATE_DATE as string}
                                            </Typography>
                                        )}
                                        {personId && canViewHistory && (
                                            <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={handleOpenHistory}>
                                                {t("pages.customers.dashboard.synthesis.history")}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </InfoCard>
                    </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <InfoCard title={t("pages.customers.dashboard.synthesis.cardsAndIds")} icon={<CreditCardIcon />}>

                            {visibleIdentifiers.length > 0 ? (
                                <Stack spacing={1}>
                                    {visibleIdentifiers.map((id, idx) => (
                                        <Box
                                            key={id.PERSON_IDENTIFIER_ID ?? idx}
                                            sx={{
                                                p: 1, borderRadius: 1,
                                                bgcolor: id.PRIORITY === "1" ? "primary.50" : "grey.50",
                                                border: "1px solid",
                                                borderColor: id.PRIORITY === "1" ? "primary.200" : "grey.200",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25, minWidth: 0 }}>
                                                <OverflowTooltip
                                                    value={id.IDENTIFIER_VALUE || "—"}
                                                    variant="body2"
                                                    fontWeight={600}
                                                    color={id.PRIORITY === "1" ? "primary.main" : "text.primary"}
                                                    sx={{ flex: 1, minWidth: 0, cursor: "default" }}
                                                />
                                                {id.PRIORITY === "1" && (
                                                    <Chip label={t("pages.customers.dashboard.synthesis.primaryCard")} size="small" color="primary" sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />
                                                )}
                                                {id.DESACTIVATION_DATE && (
                                                    <Chip label={t("pages.customers.dashboard.synthesis.deactivated")} size="small" color="error" sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {id.PERSON_IDENTIFIER_TYPE_NAME || `Type ${id.PERSON_IDENTIFIER_TYPE_ID}`}
                                            </Typography>
                                            {id.ACTIVATION_DATE && (
                                                <Typography variant="caption" color="text.disabled" display="block">
                                                    {t("pages.customers.dashboard.synthesis.activatedOn")} {id.ACTIVATION_DATE}
                                                    {id.LAST_ACTIVITY_DATE ? ` · ${t("pages.customers.dashboard.synthesis.lastActivity")} ${id.LAST_ACTIVITY_DATE}` : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            ) : cardNumber ? (
                                <InfoRow icon={<CreditCardIcon fontSize="small" />} label={t("pages.customers.dashboard.synthesis.cardNumberCol")} value={cardNumber} />
                            ) : (
                                <Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noCard")}</Typography>
                            )}
                        </InfoCard>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <InfoCard title={t("pages.customers.dashboard.synthesis.cnilAuth")} icon={<SecurityIcon />}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 2 }}>
                                {[
                                    { title: t("pages.customers.dashboard.synthesis.cnilCompany"), items: [{ icon: <LocalPostOfficeIcon />, label: t("pages.customers.dashboard.synthesis.mail"), active: cnilCompanyMail, date: npaiStatusDate, acceptLabel: "Accepte le courrier" }, { icon: <AlternateEmailIcon />, label: "Email", active: cnilCompanyEmail, date: emailStatusD, acceptLabel: "Accepte les emails" }, { icon: <SmsIcon />, label: "SMS", active: cnilCompanySms, date: cellStatusD, acceptLabel: "Accepte les SMS" }] },
                                    { title: t("pages.customers.dashboard.synthesis.cnilThird"),   items: [{ icon: <LocalPostOfficeIcon />, label: t("pages.customers.dashboard.synthesis.mail"), active: cnilTrustedMail, date: npaiStatusDate, acceptLabel: "Accepte le courrier" }, { icon: <AlternateEmailIcon />, label: "Email", active: cnilTrustedEmail, date: emailStatusD, acceptLabel: "Accepte les emails" }, { icon: <SmsIcon />, label: "SMS", active: cnilTrustedSms, date: cellStatusD, acceptLabel: "Accepte les SMS" }] },
                                ].map((group, gi) => (
                                    <Box key={gi} sx={{ flex: "1 1 190px", minWidth: 190 }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600, textTransform: "uppercase" }}>{group.title}</Typography>
                                        <Box sx={{ display: "flex", gap: 4 }}>
                                            {group.items.map(({ icon, label, active, date, acceptLabel }) => (
                                                <Tooltip key={label} title={active && date ? `${acceptLabel} depuis le ${date}` : ""} arrow>
                                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, width: 48 }}>
                                                        <Box sx={{ color: active ? "primary.main" : "text.disabled", display: "flex" }}>{icon}</Box>
                                                        <Typography variant="caption" color={active ? "primary.main" : "text.disabled"} noWrap>{label}</Typography>
                                                    </Box>
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </InfoCard>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <InfoCard title={t("pages.customers.dashboard.synthesis.subscriptions")} icon={<CheckCircleIcon />}>
                            {subscriptions.length > 0 ? (
                                <Stack spacing={1}>
                                    {subscriptions.map((sub, idx) => {
                                        const isSubscribed = sub.IS_SUBSCRIBED === "1";
                                        return (
                                            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    {isSubscribed
                                                        ? <CheckCircleIcon color="success" fontSize="small" />
                                                        : <CancelIcon color="error" fontSize="small" />
                                                    }
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2">{sub.LABEL}</Typography>
                                                    {sub.UPDATED_AT && (
                                                        <Typography variant="caption" color="text.secondary">{t("pages.customers.dashboard.synthesis.updatedAt")} {sub.UPDATED_AT}</Typography>
                                                    )}
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color={isSubscribed ? "error" : "success"}
                                                    onClick={() => sub.PER_PERSON_SUBSCRIPTIONS_ID && handleToggleSubscription(sub.PER_PERSON_SUBSCRIPTIONS_ID, sub.IS_SUBSCRIBED ?? "0", sub.LABEL ?? "")}
                                                    disabled={!sub.PER_PERSON_SUBSCRIPTIONS_ID || !!loadingSubscriptionId}
                                                >
                                                    {isSubscribed ? t("pages.customers.dashboard.synthesis.unsubscribe") : t("pages.customers.dashboard.synthesis.subscribe")}
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noSubscription")}</Typography>
                            )}
                        </InfoCard>
                    </Grid>
                    {/* ── Section badges API ── */}
                    {categoryEntries.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "divider",
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                                    <WorkspacePremiumIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.7rem" }}>
                                        {t("pages.customers.dashboard.synthesis.segmentsAndBadges")}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    {categoryEntries.map(([category, badges]) => (
                                        <Box key={category}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ fontSize: "0.72rem" }}>
                                                    {category}
                                                </Typography>
                                                {badges[0]?.CATEGORY_DESCRIPTION && (
                                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem" }}>
                                                        — {badges[0].CATEGORY_DESCRIPTION}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                {badges.map((badge, idx) => (
                                                    <Tooltip
                                                        key={idx}
                                                        title={
                                                            <Box sx={{ p: 0.5 }}>
                                                                {badge.SEGMENT_NAME && (
                                                                    <Typography variant="caption" display="block" fontWeight={700}>{badge.SEGMENT_NAME}</Typography>
                                                                )}
                                                                {badge.SEGMENT_DESCRIPTION && (
                                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{badge.SEGMENT_DESCRIPTION}</Typography>
                                                                )}
                                                                {badge.UPDATE_DATE && (
                                                                    <Typography variant="caption" color="grey.400" display="block" sx={{ mt: 0.75 }}>
                                                                        {t("pages.customers.dashboard.synthesis.updatedAt")} {formatDate(badge.UPDATE_DATE)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <Box sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            p: 1,
                                                            borderRadius: 2,
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            bgcolor: "background.paper",
                                                            cursor: "default",
                                                            width: 96,
                                                            "&:hover": {
                                                                bgcolor: "primary.50",
                                                                borderColor: "primary.light",
                                                                boxShadow: 1,
                                                            },
                                                            transition: "all 0.15s ease",
                                                        }}>
                                                            <BadgeImg img={badge.IMG} name={badge.SEGMENT_NAME} isSegment={!badge.IMG} />
                                                            <Typography variant="caption" textAlign="center" fontWeight={600} sx={{
                                                                fontSize: "0.63rem",
                                                                lineHeight: 1.3,
                                                                color: "text.secondary",
                                                                width: "100%",
                                                                whiteSpace: "normal",
                                                                wordBreak: "break-word",
                                                            }}>
                                                                {badge.SEGMENT_NAME || "—"}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <InfoCard title={t("pages.customers.dashboard.synthesis.events")} icon={<EventNoteIcon />}>
                            {events.length > 0 ? (
                                <Stack spacing={0.75}>
                                    {events.map((ev, idx) => (
                                        <Box key={idx}>
                                            {ev.CATEGORY_NAME && (idx === 0 || events[idx - 1]?.CATEGORY_NAME !== ev.CATEGORY_NAME) && (
                                                <Typography variant="caption" fontWeight={700} color="primary.main" display="block" sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                    {ev.CATEGORY_NAME}
                                                </Typography>
                                            )}
                                            <Typography variant="body2">
                                                {ev.NAME}
                                                {ev.EVENT_DATE && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                                        ({ev.EVENT_DATE})
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noEvent")}</Typography>
                            )}
                        </InfoCard>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <InfoCard title={t("pages.customers.dashboard.synthesis.household")} icon={<PeopleIcon />}>
                            {groupMembers.length > 0 ? (
                                <Stack spacing={1.5}>
                                    {groupMembers.map((member, idx) => (
                                        <Box key={idx}>
                                            {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {[member.SHORT_TITLE, member.FIRSTNAME, member.LASTNAME].filter(Boolean).join(" ")}
                                                </Typography>
                                                {member.IS_MAIN_MEMBER === "1" && (
                                                    <Chip label={t("pages.customers.dashboard.synthesis.mainMember")} size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />
                                                )}
                                            </Box>
                                            {member.IDENTIFIER_VALUE && (
                                                <Typography variant="caption" color="primary.main" display="block">{t("pages.customers.dashboard.synthesis.cardNo")}{member.IDENTIFIER_VALUE}</Typography>
                                            )}
                                            {member.BIRTH_DATE && (
                                                <Typography variant="caption" color="text.secondary" display="block">{t("pages.customers.dashboard.synthesis.bornOn")} {member.BIRTH_DATE}</Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noMember")}</Typography>
                            )}
                        </InfoCard>
                    </Grid>

                </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
                <Stack spacing={2}>

                    <InfoCard title={t("pages.customers.dashboard.synthesis.todayTransactions")} icon={<ReceiptLongIcon />} noPad>
                        {loadingRealtimeTx ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
                        ) : realtimeTransactions.length === 0 ? (
                            <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noTodayTransaction")}</Typography></Box>
                        ) : (
                            <TableContainer>
                                <Table size="small" sx={{ tableLayout: "fixed" }}>
                                    <colgroup>
                                        <col style={{ width: "25%" }} />
                                        <col style={{ width: "35%" }} />
                                        <col style={{ width: "20%" }} />
                                        <col style={{ width: "20%" }} />
                                    </colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.storeCol")}</TableCell>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.purchaseDate")}</TableCell>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.amount")}</TableCell>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.receipt")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {realtimeTransactions.map((tx, idx) => (
                                            <TableRow key={String(tx.RECEIPTNUMBER) || String(idx)} hover onClick={() => setSelectedTx(tx)} sx={{ cursor: "pointer" }}>
                                                <TooltipCell label={String(tx.STORE_NAME || tx.STORE || "")}>{String(tx.STORE_NAME || tx.STORE || "—")}</TooltipCell>
                                                <TooltipCell label={formatDate(String(tx.TRANSACTIONDATE || tx.VALIDATIONDATE || "") || undefined)}>{formatDate(String(tx.TRANSACTIONDATE || tx.VALIDATIONDATE || "") || undefined)}</TooltipCell>
                                                <TableCell sx={dataCellSx}>
                                                    <Typography variant="body2" fontWeight={600} color="primary.main" noWrap sx={{ fontSize: "inherit" }}>
                                                        {formatAmount(tx.AMOUNT as number | string | null)}
                                                    </Typography>
                                                </TableCell>
                                                <TooltipCell label={String(tx.RECEIPTNUMBER || "")}>{String(tx.RECEIPTNUMBER || "—")}</TooltipCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </InfoCard>

                    <InfoCard title={t("pages.customers.dashboard.synthesis.todayOffers")} icon={<AccountBalanceWalletIcon />} noPad>
                        {loadingRealtimeOffers ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
                        ) : realtimeOffers.length === 0 ? (
                            <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noTodayOffer")}</Typography></Box>
                        ) : (
                            <TableContainer>
                                <Table size="small" sx={{ tableLayout: "fixed" }}>
                                    <colgroup>
                                        <col style={{ width: "18%" }} />
                                        <col style={{ width: "33%" }} />
                                        <col style={{ width: "8%" }} />
                                        <col style={{ width: "28%" }} />
                                        <col style={{ width: "13%" }} />
                                    </colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.sendDate")}</TableCell>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.operationLabel")}</TableCell>
                                            <TableCell sx={headerCellSx} />
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.validity")}</TableCell>
                                            <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>{t("pages.customers.dashboard.synthesis.used")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {realtimeOffers.map((offer, idx) => (
                                            <TableRow key={offer.offerId || String(idx)} hover sx={{ bgcolor: offer.used ? (t) => alpha(t.palette.success.main, 0.1) : "inherit" }}>
                                                <TooltipCell label={formatDate(offer.startDate || offer.sendDate)}>{formatDate(offer.startDate || offer.sendDate)}</TooltipCell>
                                                <TableCell sx={{ ...dataCellSx, whiteSpace: "normal", lineHeight: 1.3 }}>
                                                    {offer.offerName || offer.offerDescription || "—"}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCellSx, overflow: "visible", px: 1, textAlign: "center" }}>
                                                    {offer.objApplication ? <CanalChip canal={offer.objApplication} /> : "—"}
                                                </TableCell>
                                                <TooltipCell label={offer.startDate && offer.endDate ? `${formatDate(offer.startDate)} ${t("pages.customers.dashboard.synthesis.to")} ${formatDate(offer.endDate)}` : formatDate(offer.endDate)}>
                                                    {offer.startDate && offer.endDate
                                                        ? `${formatDate(offer.startDate)} ${t("pages.customers.dashboard.synthesis.to")} ${formatDate(offer.endDate)}`
                                                        : formatDate(offer.endDate)}
                                                </TooltipCell>
                                                <TableCell sx={{ ...dataCellSx, textAlign: "center" }}>
                                                    {offer.used
                                                        ? <CheckCircleIcon fontSize="small" color="success" />
                                                        : <CancelIcon fontSize="small" color="error" />
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </InfoCard>

                    <InfoCard title={t("pages.customers.dashboard.synthesis.lastOffers")} icon={<AccountBalanceWalletIcon />} noPad>
                        {loadingOffers ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
                        ) : offers.length === 0 ? (
                            <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noOperation")}</Typography></Box>
                        ) : (
                            <TableContainer>
                                <Table size="small" sx={{ tableLayout: "fixed" }}>
                                    <colgroup>
                                        <col style={{ width: "18%" }} />
                                        <col style={{ width: "32%" }} />
                                        <col style={{ width: "8%" }} />
                                        <col style={{ width: "32%" }} />
                                        <col style={{ width: "10%" }} />
                                    </colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.sendDate")}</TableCell>
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.operationLabel")}</TableCell>
                                            <TableCell sx={headerCellSx} />{/* Canal — icône uniquement, pas de titre */}
                                            <TableCell sx={headerCellSx}>{t("pages.customers.dashboard.synthesis.validity")}</TableCell>
                                            <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>{t("pages.customers.dashboard.synthesis.used")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...offers].sort((a, b) => parseDMY(b.SENTDATE || b.TRDATE) - parseDMY(a.SENTDATE || a.TRDATE)).slice(0, 6).map((offer, idx) => {
                                            const isUsed = offer.ISUSED === true || offer.ISUSED === "true" || offer.ISUSED === "1" || offer.ISUSED === 1;
                                            return (
                                            <TableRow key={offer.NUMOPERATION || idx} hover sx={{ bgcolor: isUsed ? (t) => alpha(t.palette.success.main, 0.1) : undefined }}>
                                                <TooltipCell label={formatDate(offer.SENTDATE || offer.TRDATE)}>{formatDate(offer.SENTDATE || offer.TRDATE)}</TooltipCell>
                                                <TableCell sx={{ ...dataCellSx, whiteSpace: "normal", lineHeight: 1.3 }}>
                                                    {offer.OPERATIONNAME || "—"}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCellSx, overflow: "visible", px: 1, textAlign: "center" }}>
                                                    {offer.CANAL ? <CanalChip canal={offer.CANAL} /> : "—"}
                                                </TableCell>
                                                <TooltipCell label={offer.STARTDATE && offer.ENDDATE ? `${formatDate(offer.STARTDATE)} ${t("pages.customers.dashboard.synthesis.to")} ${formatDate(offer.ENDDATE)}` : formatDate(offer.STARTDATE || offer.ENDDATE)}>
                                                    {offer.STARTDATE && offer.ENDDATE
                                                        ? `${formatDate(offer.STARTDATE)} ${t("pages.customers.dashboard.synthesis.to")} ${formatDate(offer.ENDDATE)}`
                                                        : formatDate(offer.STARTDATE || offer.ENDDATE)}
                                                </TooltipCell>
                                                <TableCell sx={{ ...dataCellSx, textAlign: "center" }}>
                                                    {isUsed
                                                        ? (
                                                            <Tooltip arrow title={`Utilisé le ${formatDateWithHours(offer.TRDATE as string)}`}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </Tooltip>
                                                        )
                                                        : <CancelIcon fontSize="small" color="error" />
                                                    }
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                            {handleOpenMvt && (
                                <Button variant="outlined" size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenMvt}>
                                    {t("pages.customers.dashboard.synthesis.addPointsMovement")}
                                </Button>
                            )}
                            <Button variant="text" size="small" onClick={() => setActiveTab(2)}>
                                {t("pages.customers.dashboard.synthesis.seeAllOperations")} →
                            </Button>
                        </Box>
                    </InfoCard>

                    <InfoCard title={t("pages.customers.dashboard.synthesis.lastTransactions")} icon={<ReceiptLongIcon />} noPad>
                        {loadingTransac ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
                        ) : transactions.length === 0 ? (
                            <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{t("pages.customers.dashboard.synthesis.noTransaction")}</Typography></Box>
                        ) : (
                            <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
                                    <colgroup>
                                        <col style={{ width: "16%" }} />
                                        <col style={{ width: "18%" }} />
                                        <col style={{ width: "18%" }} />
                                        <col style={{ width: "16%" }} />
                                        <col style={{ width: "16%" }} />
                                    </colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>{t("pages.customers.dashboard.synthesis.storeCol")}</TableCell>
                                            <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>Date de réception</TableCell>
                                            <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>{t("pages.customers.dashboard.synthesis.purchaseDate")}</TableCell>
                                            <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>{t("pages.customers.dashboard.synthesis.paidAmount")}</TableCell>
                                            <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap", textAlign: "center" }}>Opération Liée</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.slice(0, 5).map((tx, idx) => {
                                            const hasOffer = Number(tx.COUNTLINKOFFER ?? 0) > 0;
                                            return (
                                                <TableRow
                                                    key={tx.RECEIPTNUMBER || idx}
                                                    hover
                                                    onClick={() => setSelectedTx(tx)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        bgcolor: hasOffer ? (t) => alpha(t.palette.warning.main, 0.35) : undefined,
                                                        "&:hover": { bgcolor: hasOffer ? (t) => `${alpha(t.palette.warning.main, 0.5)} !important` : undefined },
                                                    }}
                                                >
                                                    <TooltipCell label={String(tx.STORE_NAME || tx.STORE || "")}>{tx.STORE_NAME || tx.STORE || "—"}</TooltipCell>
                                                    <TooltipCell label={formatDateWithHours(tx.VALIDATIONDATE)}>{formatDateWithHours(tx.VALIDATIONDATE)}</TooltipCell>
                                                    <TooltipCell label={formatDateWithHours(tx.TRANSACTIONDATE)}>{formatDateWithHours(tx.TRANSACTIONDATE)}</TooltipCell>
                                                    <TableCell sx={dataCellSx}>
                                                        <Typography variant="body2" fontWeight={600} color="primary.main" noWrap sx={{ fontSize: "inherit" }}>
                                                            {formatAmount(tx.AMOUNT as number | string | null)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCellSx, overflow: "visible", textAlign: "center" }}>
                                                        {hasOffer ? (
                                                            <Chip label={String(tx.COUNTLINKOFFER)} size="small" color="warning" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                                                        ) : "—"}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                            {handleOpenManualTransac && (
                                <Button variant="outlined" size="small" startIcon={<PointOfSaleIcon />} onClick={handleOpenManualTransac}>
                                    {t("pages.customers.dashboard.synthesis.addTransaction")}
                                </Button>
                            )}
                            <Button variant="text" size="small" onClick={() => setActiveTab(1)}>
                                {t("pages.customers.dashboard.synthesis.seeAllTransactions")} →
                            </Button>
                        </Box>
                    </InfoCard>

                </Stack>
            </Grid>

        </Grid>
    </TabPanel>

    <PersonHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
        isLoading={loadingHistory}
        error={historyError}
    />
    {storeDialogOpen && (
        <StoreDetailDialog
            store={store}
            open={storeDialogOpen}
            onClose={() => setStoreDialogOpen(false)}
            loading={storeDialogLoading}
        />
    )}
    </>
    );
};

export default SynthesisTab;
